import React, { FC, Suspense, useCallback } from 'react';
import Loader from 'src/components/widgets/loader';
import config from 'src/shared/config';
import { T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import { Dialog, useDialogManager } from '../../../../widgets/dialog';
import ServiceCredits from '../../../myAccount/serviceCredits';
import { DialogContent } from '../shared/sharedStyles';

const NO_CREDITS_DIALOG_ID = 'ai-studio-no-credits';

// ServiceCredits renders a fragment, so its sections (badge, title, credits, actions) are direct
// flex children of DialogContent. The shared 20px gap leaves too much air between them in this
// compact purchase dialog, so tighten it locally without touching the shared DialogContent.
const NoCreditsDialogContent = styled(DialogContent)`
    gap: 8px;
`;

interface AIStudioNoCreditsDialogProps {
    onClose: () => void;
    /** Dialog heading. Defaults to the generic out-of-credits copy; callers can override it to fit the context. */
    title?: string;
}

/**
 * Shown when a task is started but the backend reports NO_CREDITS. Embeds the same credit-purchase
 * component used in the My Account → Subscriptions addons section so the seller can top up AI Studio
 * credits without leaving the studio. The AI_STUDIO service config mirrors subscriptions.tsx.
 */
const AIStudioNoCreditsDialog: FC<AIStudioNoCreditsDialogProps> = ({ onClose, title }) => {
    const { subscription, ecommerceName, shopifyEcommerceSettings, salesChannels } = useSubscriptionData();
    const isShopify = ecommerceName === 'SHOPIFY';
    const isTrial = subscription?.inTrial ?? false;
    const isShopifyTestStore = isShopify && shopifyEcommerceSettings?.isTestStore === true && config.isProduction;
    const isDisabled = (isTrial && !isShopify) || isShopifyTestStore;

    return (
        <Dialog title={title ?? T._('Out of AI Agents Studio credits', 'Admin')} onClose={onClose}>
            <NoCreditsDialogContent>
                <Suspense fallback={<Loader />}>
                    <ServiceCredits
                        onPurchaseCompleted={onClose}
                        services={[
                            {
                                serviceName: 'AI_STUDIO',
                                addonKey: 'AI_STUDIO',
                                displayName: 'AI Agents Studio',
                                description: <>{T._('Generate visuals, videos and 3D models with AI Agents Studio tools.', 'Admin')}</>,
                                learnMoreUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/25239933114908-AI-Agents-Studio',
                                addonCurrencyCode: 'USD',
                                disabled: isDisabled,
                                disabledMessage: isShopifyTestStore
                                    ? T._('Sorry! Your account is currently a development store, which means you can not purchase credits.', 'Admin')
                                    : isTrial
                                      ? T._('Unable to purchase credits during the trial period.', 'Admin')
                                      : undefined,
                                notAvailableInTrial: isDisabled
                            }
                        ]}
                        subscriptionCurrencyCode={
                            isShopify ? (salesChannels.at(0)?.ecommerceSettings?.currencyCode ?? 'USD') : (subscription?.plan?.currencyCode ?? 'USD')
                        }
                        isShopify={isShopify}
                        multiservice={false}
                    />
                </Suspense>
            </NoCreditsDialogContent>
        </Dialog>
    );
};

/** Returns a stable callback that opens the AI Studio no-credits purchase dialog with an optional custom title. */
export function useAIStudioCreditsDialog(): { showNoCreditsDialog: (title?: string) => void } {
    const { showDialog, closeDialog } = useDialogManager();

    const showNoCreditsDialog = useCallback(
        (title?: string) => {
            showDialog(NO_CREDITS_DIALOG_ID, <AIStudioNoCreditsDialog title={title} onClose={() => closeDialog(NO_CREDITS_DIALOG_ID)} />);
        },
        [showDialog, closeDialog]
    );

    return { showNoCreditsDialog };
}
